<script setup>
import { ref, onMounted } from 'vue';
import { Head } from '@inertiajs/vue3';
import axios from 'axios';

// State
const keywords = ref([]);
const loading = ref(true);
const saving = ref(false);
const isEditing = ref(false);

// Form state
const form = ref({
    id: null,
    keyword: '',
    url: '',
    description: ''
});

// Methods
const loadKeywords = async () => {
    loading.value = true;
    try {
        const response = await axios.get('/admin/auto-link-keywords/get');
        keywords.value = response.data;
    } catch (error) {
        console.error('Error loading keywords:', error);
    } finally {
        loading.value = false;
    }
};

const resetForm = () => {
    form.value = { id: null, keyword: '', url: '', description: '' };
    isEditing.value = false;
};

const editKeyword = async (id) => {
    try {
        const response = await axios.get(`/admin/auto-link-keywords/${id}`);
        if (response.data.status === 'success') {
            const kw = response.data.data;
            form.value = {
                id: kw.id,
                keyword: kw.keyword,
                url: kw.url,
                description: kw.description || ''
            };
            isEditing.value = true;
        }
    } catch (error) {
        alert('Gagal memuat data');
    }
};

const submitForm = async () => {
    saving.value = true;
    
    let url = '/admin/auto-link-keywords';
    const data = { ...form.value };
    
    if (isEditing.value) {
        data._method = 'PUT';
        url = `/admin/auto-link-keywords/${form.value.id}`;
    }
    
    try {
        const response = await axios.post(url, data);
        if (response.data.status === 'success') {
            alert(response.data.message);
            resetForm();
            loadKeywords();
        } else if (response.data.status === 'info') {
            alert(response.data.message);
        }
    } catch (error) {
        if (error.response?.data?.messages) {
            alert(error.response.data.messages.join('\n'));
        } else {
            alert('Terjadi kesalahan');
        }
    } finally {
        saving.value = false;
    }
};

const toggleActive = async (keyword) => {
    try {
        const response = await axios.post(`/admin/auto-link-keywords/${keyword.id}/toggle`);
        if (response.data.status === 'success') {
            loadKeywords();
        }
    } catch (error) {
        alert('Gagal mengubah status');
    }
};

const deleteKeyword = async (keyword) => {
    if (!confirm(`Hapus keyword "${keyword.keyword}"?`)) return;
    
    try {
        const response = await axios.delete(`/admin/auto-link-keywords/${keyword.id}`);
        if (response.data.status === 'success') {
            alert(response.data.message);
            loadKeywords();
        }
    } catch (error) {
        alert('Gagal menghapus');
    }
};

onMounted(() => {
    loadKeywords();
});
</script>

<template>
    <Head title="Auto Link Keywords" />

    <div class="container-full">
        <div class="content-header">
            <div class="d-flex align-items-center">
                <div class="me-auto">
                    <h3 class="page-title">Auto Link Keywords</h3>
                    <nav>
                        <ol class="breadcrumb">
                            <li class="breadcrumb-item"><a href="/admin/dashboard"><i class="mdi mdi-home-outline"></i></a></li>
                            <li class="breadcrumb-item active">Auto Link Keywords</li>
                        </ol>
                    </nav>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="row">
                <!-- Form Panel -->
                <div class="col-xl-4 col-12">
                    <div class="card">
                        <form @submit.prevent="submitForm">
                            <div class="card-header">
                                <h4 class="card-title">
                                    <i class="ti-link me-5"></i>{{ isEditing ? 'Edit' : 'Tambah' }} Keyword
                                </h4>
                            </div>
                            <div class="card-body">
                                <div class="form-group mb-3">
                                    <label class="form-label">Keyword <span class="text-danger">*</span></label>
                                    <input type="text" v-model="form.keyword" class="form-control" 
                                        placeholder="Contoh: rental mobil" required>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">URL <span class="text-danger">*</span></label>
                                    <input type="url" v-model="form.url" class="form-control" 
                                        placeholder="https://example.com/page" required>
                                </div>
                                
                                <div class="form-group mb-3">
                                    <label class="form-label">Deskripsi</label>
                                    <textarea v-model="form.description" class="form-control" rows="2"
                                        placeholder="Keterangan opsional"></textarea>
                                </div>
                            </div>
                            <div class="card-footer text-end">
                                <button v-if="isEditing" type="button" class="btn btn-secondary me-1" @click="resetForm">
                                    Batal
                                </button>
                                <button type="submit" class="btn btn-primary" :disabled="saving">
                                    <i v-if="saving" class="fa fa-spinner fa-spin me-1"></i>
                                    <i v-else class="ti-save-alt me-1"></i>
                                    Simpan
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                
                <!-- Table Panel -->
                <div class="col-xl-8 col-12">
                    <div class="card">
                        <div class="card-header">
                            <h4 class="card-title"><i class="ti-view-list me-10"></i>Daftar Keywords</h4>
                        </div>
                        <div class="card-body">
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Keyword</th>
                                            <th>URL</th>
                                            <th>Status</th>
                                            <th>Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="loading">
                                            <td colspan="5" class="text-center p-4">
                                                <div class="spinner-border text-primary"></div>
                                            </td>
                                        </tr>
                                        <tr v-else-if="keywords.length === 0">
                                            <td colspan="5" class="text-center text-muted p-4">
                                                Belum ada keyword
                                            </td>
                                        </tr>
                                        <tr v-else v-for="(kw, index) in keywords" :key="kw.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>
                                                <strong>{{ kw.keyword }}</strong>
                                                <div v-if="kw.description" class="small text-muted">{{ kw.description }}</div>
                                            </td>
                                            <td>
                                                <a :href="kw.url" target="_blank" class="text-primary small">
                                                    {{ kw.url.length > 40 ? kw.url.substring(0, 40) + '...' : kw.url }}
                                                </a>
                                            </td>
                                            <td>
                                                <button type="button" 
                                                    :class="['btn', 'btn-sm', 'btn-toggle', kw.is_active ? 'active' : '']"
                                                    @click="toggleActive(kw)">
                                                    <span class="handle"></span>
                                                </button>
                                            </td>
                                            <td>
                                                <div class="btn-group btn-group-sm">
                                                    <button class="btn btn-outline-primary" @click="editKeyword(kw.id)">
                                                        <i class="fa fa-edit"></i>
                                                    </button>
                                                    <button class="btn btn-outline-danger" @click="deleteKeyword(kw)">
                                                        <i class="fa fa-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>
